import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { adminSeasonCommunicationsAPI } from '../services/adminApi';

const Panel = styled.div`
  padding: 1.5rem;
`;

const Title = styled.h2`
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
`;

const Help = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const Alert = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  background: ${(p) => (p.$warn ? '#fef3c7' : '#e0f2fe')};
  color: ${(p) => (p.$warn ? '#92400e' : '#0369a1')};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1rem;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  width: 4rem;
  font-size: 0.875rem;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background: #043663;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #032a4f;
  }
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.65rem 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
`;

const Td = styled.td`
  padding: 0.6rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
`;

/**
 * League reminder: teams with at most N completed matches (default 1). Gilles / April 19 copy; Reply-To via HPS_REPLY_TO_EMAIL.
 */
export default function AdminSeasonTeamEmailsPanel({ seasonId, readOnly = false }) {
  const queryClient = useQueryClient();
  const [maxCompleted, setMaxCompleted] = useState(1);

  const previewQuery = useQuery(
    ['admin-low-match-teams', seasonId, maxCompleted],
    () => adminSeasonCommunicationsAPI.getLowMatchCountTeams(seasonId, { max_completed: maxCompleted }),
    {
      enabled: !!seasonId,
      staleTime: 30 * 1000,
      onError: (e) => toast.error(e.response?.data?.message || 'Could not load team list'),
    }
  );

  const payload = previewQuery.data;
  const teams = payload?.data?.teams ?? [];
  const replyConfigured = payload?.data?.reply_to_configured;

  const sendMutation = useMutation(
    () =>
      adminSeasonCommunicationsAPI.sendLowMatchReminder(seasonId, {
        send: true,
        max_completed: maxCompleted,
      }),
    {
      onSuccess: (res) => {
        const d = res?.data;
        toast.success(`Sent ${d?.sent_ok ?? 0} / ${d?.total ?? 0} email(s).`);
        queryClient.invalidateQueries(['admin-low-match-teams', seasonId, maxCompleted]);
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Send failed'),
    }
  );

  const onSend = () => {
    if (readOnly) return;
    if (!window.confirm(`Send the phase reminder email to ${teams.length} team(s)? This cannot be undone.`)) return;
    sendMutation.mutate();
  };

  return (
    <Panel>
      <Title>Team emails – match progress reminder</Title>
      <Help>
        Lists teams with at most the given number of <strong>completed</strong> matches in this phase (0 = none played,
        1 = one or fewer). Sends the “April 19 / six matches / forfeit” message from Gilles. Recipients are active{' '}
        <strong>team member</strong> emails when available, otherwise the team account email.
      </Help>

      {!replyConfigured && (
        <Alert $warn>
          Set server env <code>HPS_REPLY_TO_EMAIL</code> (or <code>HPS_OPERATIONS_REPLY_TO</code>) to your operations
          Outlook address so <strong>Reply-To</strong> points there; otherwise “Reply” in the inbox may target only the
          noreply sender.
        </Alert>
      )}
      {replyConfigured && (
        <Alert>
          Reply-To is configured on the server. Teams can use “Reply”; you should receive answers in that mailbox (same
          rules as any mail: SPF/DKIM, SES verification).
        </Alert>
      )}

      <Row>
        <label htmlFor="max-completed-input" style={{ fontSize: '0.875rem', color: '#374151' }}>
          Max completed matches (inclusive)
        </label>
        <Input
          id="max-completed-input"
          type="number"
          min={0}
          max={20}
          value={maxCompleted}
          onChange={(e) => setMaxCompleted(Math.min(20, Math.max(0, parseInt(e.target.value, 10) || 0)))}
          disabled={readOnly || sendMutation.isLoading}
        />
        <Button type="button" onClick={() => previewQuery.refetch()} disabled={previewQuery.isFetching || readOnly}>
          Refresh list
        </Button>
        <Button
          type="button"
          onClick={onSend}
          disabled={readOnly || teams.length === 0 || sendMutation.isLoading || previewQuery.isLoading}
        >
          {sendMutation.isLoading ? 'Sending…' : `Send emails (${teams.length})`}
        </Button>
      </Row>

      {previewQuery.isLoading && <p style={{ color: '#6b7280' }}>Loading…</p>}
      {previewQuery.isError && <p style={{ color: '#b91c1c' }}>Could not load teams.</p>}

      {!previewQuery.isLoading && teams.length === 0 && (
        <p style={{ color: '#6b7280' }}>No teams match this threshold for the selected phase.</p>
      )}

      {teams.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Team</Th>
                <Th>Completed matches</Th>
                <Th>Team email</Th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.team_id}>
                  <Td>{t.team_name}</Td>
                  <Td>{t.completed_matches}</Td>
                  <Td style={{ wordBreak: 'break-all' }}>{t.team_email || '—'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Panel>
  );
}
